import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token — request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Use at least 6 characters.");
      return;
    }
    if (pw !== pw2) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: pw });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5"
         style={{ background: "var(--bg)" }} data-testid="reset-page">
      <div className="w-card w-card-bordered p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl grid place-items-center"
               style={{ background: "var(--navy)", color: "#fff" }}>
            {done ? <CheckCircle2 className="h-5 w-5" /> : <KeyRound className="h-5 w-5" />}
          </div>
          <div>
            <div className="label-caps gold">Zola Rewards</div>
            <h1 className="text-2xl font-bold" style={{ letterSpacing: "-0.02em" }}>
              {done ? "Password updated" : "Set a new password"}
            </h1>
          </div>
        </div>

        {done ? (
          <>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>
              Your password has been updated. Head back to sign in with the new one.
            </p>
            <div className="mt-6 flex gap-2">
              <Link to="/login/customer" data-testid="reset-goto-customer"
                    className="rounded-full h-10 px-5 grid place-items-center text-sm font-semibold text-white"
                    style={{ background: "var(--navy)" }}>
                Customer sign in
              </Link>
              <Link to="/login/staff"
                    className="rounded-full h-10 px-5 grid place-items-center text-sm font-semibold"
                    style={{ border: "1px solid var(--border-strong)", color: "var(--text)" }}>
                Staff sign in
              </Link>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label htmlFor="pw">New password</Label>
              <Input id="pw" data-testid="reset-pw-input" type="password"
                     value={pw} onChange={(e) => setPw(e.target.value)}
                     minLength={6} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="pw2">Confirm password</Label>
              <Input id="pw2" data-testid="reset-pw2-input" type="password"
                     value={pw2} onChange={(e) => setPw2(e.target.value)}
                     minLength={6} required />
            </div>
            <Button type="submit" disabled={loading || !token}
                    data-testid="reset-submit"
                    className="w-full h-11 rounded-xl text-white"
                    style={{ background: "var(--navy)" }}>
              {loading ? "Updating…" : "Update password"}
            </Button>
            <button type="button" onClick={() => nav("/")}
                    className="w-full text-xs" style={{ color: "var(--text-muted)" }}>
              Cancel
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
